import React, { ReactNode, useMemo } from "react";
import styled from "@emotion/styled";
import InfiniteScroll from "react-infinite-scroll-component";
import { format } from "date-fns";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import { GroupPeriod, groupByMonth } from "./groupByMonth";

// Start loading the next page about two screens before the list ends.
const LOAD_AHEAD = "1600px";

// Runs to the screen edges, past the page's 16px gutter. Pulled up by its
// border width, so its top border covers the shorter divider of the row above.
const MonthHeader = styled.div(({ theme }) => ({
  margin: theme.spacing(0, -4),
  marginTop: -1,
  padding: theme.spacing(1.5, 4),
  backgroundColor: theme.palette.background.subtle,
  borderTop: `1px solid ${theme.palette.border.secondary}`,
  borderBottom: `1px solid ${theme.palette.border.secondary}`,
}));

const Group = styled("div", {
  shouldForwardProp: (prop) => prop !== "ungrouped",
})<{ ungrouped: boolean }>(({ theme, ungrouped }) =>
  ungrouped ? { borderTop: `1px solid ${theme.palette.border.secondary}` } : {}
);

const SkeletonRow = styled("div", {
  shouldForwardProp: (prop) => prop !== "withLeading",
})<{ withLeading: boolean }>(({ theme, withLeading }) => ({
  display: "grid",
  gridTemplateColumns: withLeading
    ? `${theme.spacing(8)} minmax(0, 1fr)`
    : "minmax(0, 1fr)",
  columnGap: theme.spacing(3),
  alignItems: "center",
  padding: theme.spacing(2.5, 0),
  borderBottom: `1px solid ${theme.palette.border.secondary}`,
}));

const LoadingRow = ({
  widths,
  withLeading,
}: {
  widths: [string, string, string];
  withLeading: boolean;
}) => (
  <SkeletonRow withLeading={withLeading}>
    {withLeading && <Skeleton variant="rounded" width={32} height={32} />}
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" justifyContent="space-between">
        <Skeleton variant="rounded" width={widths[0]} height={12} />
        <Skeleton variant="rounded" width={widths[1]} height={12} />
      </Box>
      <Skeleton variant="rounded" width={widths[2]} height={10} />
    </Box>
  </SkeletonRow>
);

interface Props<T> {
  items: T[];
  getDate?: (item: T) => string;
  getId: (item: T) => string | number;
  renderItem: (item: T) => ReactNode;
  groupByMonth?: boolean;
  period?: GroupPeriod;
  emptyText: ReactNode;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  loadingMoreText?: ReactNode;
  // The scroll only asks again once the list grows, so a failed page needs a
  // way to retry.
  loadMoreFailed?: { message: ReactNode; retryLabel: string };
  endText?: ReactNode;
  withLeading?: boolean;
  loadingTestId?: string;
}

export const MonthGroupedList = <T,>({
  items,
  getDate,
  getId,
  renderItem,
  groupByMonth: isGrouped = true,
  period = "month",
  emptyText,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  loadingMoreText,
  loadMoreFailed,
  endText,
  withLeading = true,
  loadingTestId,
}: Props<T>) => {
  // Page loading can bring an item twice; it is listed once either way.
  const months = useMemo(() => {
    if (isGrouped && getDate) {
      return groupByMonth(items, getDate, getId, period);
    }
    const seen = new Set<string | number>();
    const unique = items.filter((item) => {
      const id = getId(item);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    return [{ key: "all", date: new Date(), items: unique }];
  }, [items, getDate, getId, isGrouped, period]);

  if (items.length === 0) {
    return (
      <Typography
        variant="body3"
        color="textSecondary"
        textAlign="center"
        py={6}
      >
        {emptyText}
      </Typography>
    );
  }

  const rows = (
    <>
      {months.map((month) => (
        <Group key={month.key} ungrouped={month.key === "all"}>
          {month.key !== "all" && (
            <MonthHeader>
              <Typography
                variant="caption2"
                color="textSecondary"
                component="div"
              >
                {format(month.date, period === "year" ? "yyyy" : "LLLL yyyy")}
              </Typography>
            </MonthHeader>
          )}
          {month.items.map((item) => (
            <React.Fragment key={getId(item)}>
              {renderItem(item)}
            </React.Fragment>
          ))}
        </Group>
      ))}

      {isLoadingMore && (
        <Box data-testid={loadingTestId}>
          <LoadingRow
            widths={["55%", "22%", "70%"]}
            withLeading={withLeading}
          />
          <LoadingRow
            widths={["45%", "20%", "60%"]}
            withLeading={withLeading}
          />
          {loadingMoreText && (
            <Typography
              role="status"
              variant="caption"
              color="textSecondary"
              component="div"
              textAlign="center"
              pt={2.5}
            >
              {loadingMoreText}
            </Typography>
          )}
        </Box>
      )}
      {loadMoreFailed && !isLoadingMore && onLoadMore && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={1}
          pt={4}
        >
          <Typography role="alert" variant="caption" color="textSecondary">
            {loadMoreFailed.message}
          </Typography>
          <Button variant="text" size="small" onClick={onLoadMore}>
            {loadMoreFailed.retryLabel}
          </Button>
        </Box>
      )}
      {endText && !hasMore && (
        <Typography
          variant="caption"
          color="textSecondary"
          component="div"
          textAlign="center"
          pt={4}
        >
          {endText}
        </Typography>
      )}
    </>
  );

  return onLoadMore ? (
    <InfiniteScroll
      dataLength={items.length}
      next={onLoadMore}
      hasMore={hasMore}
      loader={null}
      scrollThreshold={LOAD_AHEAD}
      // The month headers run past the list's edges.
      style={{ overflow: "visible" }}
    >
      {rows}
    </InfiniteScroll>
  ) : (
    rows
  );
};
